import React from 'react';
import ContentMedia from '../ContentMedia';
import { colors, spacing, radius, typography, transitions } from '../../styles/theme';

/**
 * ModelItem - Unified card component for both models and workflows
 * Uses the unified item structure from API (model_* fields)
 * 
 * @param {Object} item - Unified item with model_id, model_name, model_cover_image_url, etc.
 * @param {string} size - 'small' | 'medium' | 'large' | 'featured'
 * @param {Function} onClick - Click handler
 * @param {boolean} showBadge - Whether to show MODEL badge for models
 */
export default function ModelItem({ item, size = 'medium', onClick, showBadge = true }) {
  if (!item) return null;

  const isWorkflow = item.item_type === 'workflow';
  const badge = showBadge && isWorkflow ? 'Flow' : null;

  const sizeStyles = SIZE_CONFIG[size] || SIZE_CONFIG.medium;

  return (
    <div onClick={onClick} style={{ ...styles.card, ...sizeStyles.card }}>
      <div style={{ ...styles.imageContainer, ...sizeStyles.imageContainer }}>
        <ContentMedia
          videoUrl={item.model_video_url}
          coverImage={item.model_cover_image_url}
          cover2Image={item.model_cover_2_image_url}
          thumbnailImage={item.model_thumbnail_image_url}
        />
        {badge && <div style={{ ...styles.badge, ...sizeStyles.badge }}>{badge}</div>}
      </div>
      <div style={{ ...styles.content, ...sizeStyles.content }}>
        <div style={{ ...styles.title, ...sizeStyles.title }}>{item.model_name}</div>
        {item.model_description && sizeStyles.showDescription && (
          <div style={{ ...styles.description, ...sizeStyles.description }}>
            {item.model_description}
          </div>
        )}
        {sizeStyles.showMeta ? (
          <div style={styles.meta}>
            {item.average_cost ? (
              <span style={styles.metaItem}>~{Number(item.average_cost).toFixed(3)} URO</span>
            ) : null}
            {item.model_eta ? (
              <span style={styles.metaItem}>~{item.model_eta}s</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const SIZE_CONFIG = {
  small: {
    card: {},
    imageContainer: { aspectRatio: '1 / 1' },
    badge: { 
      top: spacing.xs, 
      left: spacing.xs, 
      padding: `${spacing.xxs}px ${spacing.xs}px`,
      fontSize: typography.fontSize.xs - 2,
    },
    content: { padding: spacing.xs },
    title: { fontSize: typography.fontSize.sm },
    description: {},
    showDescription: false,
    showMeta: false,
  },
  medium: {
    card: {},
    imageContainer: { aspectRatio: '1 / 1' },
    badge: { 
      top: spacing.xs, 
      left: spacing.xs, 
      padding: `${spacing.xxs}px ${spacing.xs}px`,
      fontSize: typography.fontSize.xs - 1,
    },
    content: { padding: spacing.sm },
    title: { fontSize: typography.fontSize.md - 1 },
    description: { marginTop: 2 },
    showDescription: false,
    showMeta: true,
  },
  large: {
    card: {},
    imageContainer: { aspectRatio: '3 / 2' },
    badge: { 
      top: spacing.sm, 
      left: spacing.sm, 
      padding: `5px ${spacing.sm}px`,
      fontSize: typography.fontSize.xs,
    },
    content: { padding: spacing.sm },
    title: { fontSize: typography.fontSize.lg },
    description: { marginTop: spacing.xxs },
    showDescription: true,
    showMeta: true,
  },
  featured: {
    card: {},
    imageContainer: { aspectRatio: '16 / 10' },
    badge: { 
      top: spacing.sm, 
      left: spacing.sm, 
      padding: `5px ${spacing.sm}px`,
      fontSize: typography.fontSize.xs,
    },
    content: { padding: spacing.sm },
    title: { fontSize: typography.fontSize.lg },
    description: { marginTop: spacing.xxs },
    showDescription: true,
    showMeta: true,
  },
};

const styles = {
  card: {
    borderRadius: radius.md,
    overflow: 'hidden',
    cursor: 'pointer',
    background: colors.background.secondary,
    transition: transitions.normal,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
  },
  badge: {
    position: 'absolute',
    background: colors.brand.accent,
    borderRadius: radius.xs,
    fontWeight: typography.fontWeight.bold,
    color: '#000',
  },
  content: {},
  title: {
    fontWeight: typography.fontWeight.semibold,
    color: colors.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: typography.fontSize.xs,
    color: colors.text.tertiary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  metaItem: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
};
